import Dialog from "@/components/shared/dialog/Dialog";
import { Step } from "@/v2/features/sampling/Step";
import { Button, Em, Flex, Text } from "@radix-ui/themes";
import { PlusIcon } from "@radix-ui/react-icons";
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
  FilterOperator,
  FilterRuleDialogProps,
  isMessageFilterKnownValue,
  MessageFilter,
  MessageFilterRule,
  isValuelessOperator,
} from "./SystemMessage.model";
import { FilterRuleConditionRow } from "./FilterRuleConditionRow";
import {
  FilterRuleMessageStep,
  KnownMessagePlaceholder,
} from "./FilterRuleMessageStep";
import filterRuleDialogStyles from "./FilterRuleDialog.module.scss";
import { useGetQuackChatFieldOptions } from "./useQuackChatSystemMessage";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { FormField } from "@/v2/components/form/form-schema";

const getInitialFilters = (
  editingRule: MessageFilterRule | null | undefined
): MessageFilter[] => {
  if (editingRule && editingRule.conditions.length > 0) {
    return [...editingRule.conditions];
  }
  return [];
};

export function FilterRuleDialog({
  open,
  editingRule,
  isSavingFilter,
  onClose,
  onSave,
  selectedMessageType,
}: FilterRuleDialogProps) {
  const fieldOptions = useGetQuackChatFieldOptions(selectedMessageType);

  const [filters, setFilters] = useState<MessageFilter[]>([]);
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (open) {
      setFilters(getInitialFilters(editingRule));
      setMessage(editingRule?.message ?? "");
    }
  }, [open, editingRule]);

  const addCondition = useCallback((field: FormField) => {
    const defaultOperator: FilterOperator = "eq";
    setFilters((prev) => [
      ...prev,
      { id: field.id, field: field.id, operator: defaultOperator, value: "" },
    ]);
  }, []);

  const updateFilter = useCallback(
    (filterIndex: number, updates: Partial<MessageFilter>) => {
      setFilters((prevFilters) =>
        prevFilters.map((filter, index) =>
          index === filterIndex ? { ...filter, ...updates } : filter
        )
      );
    },
    []
  );

  const removeFilter = useCallback((filterIndex: number) => {
    setFilters((prev) => prev.filter((_, index) => index !== filterIndex));
  }, []);

  const getFieldConfig = useCallback(
    (fieldId: string): FormField | undefined => {
      return fieldOptions?.find((f) => f.id === fieldId);
    },
    [fieldOptions]
  );

  const contextFields = useMemo(
    () => fieldOptions ?? [],
    [fieldOptions]
  );

  const knownMessagePlaceholders = useMemo((): KnownMessagePlaceholder[] => {
    if (!fieldOptions?.length) {
      return [];
    }
    const seen = new Set<string>();
    const out: KnownMessagePlaceholder[] = [];

    for (const f of filters) {
      if (isValuelessOperator(f.operator) || !isMessageFilterKnownValue(f.value)) {
        continue;
      }

      if (f.knownSourceFieldId) {
        if (seen.has(f.knownSourceFieldId)) {
          continue;
        }
        const ctx = fieldOptions.find((opt) => opt.id === f.knownSourceFieldId);
        if (ctx) {
          seen.add(f.knownSourceFieldId);
          out.push({
            fieldId: f.knownSourceFieldId,
            label: ctx.label,
            token: `{${ctx.label}}`,
          });
        }
        continue;
      }

      const conditionFieldKey = `condition:${f.field}`;
      if (seen.has(conditionFieldKey)) {
        continue;
      }
      const condField = fieldOptions.find((opt) => opt.id === f.field);
      if (condField) {
        seen.add(conditionFieldKey);
        out.push({
          fieldId: conditionFieldKey,
          label: condField.label,
          token: `{${condField.label}}`,
        });
      }
    }

    return out;
  }, [fieldOptions, filters]);

  const handleMessageChange = useCallback((value: string) => {
    setMessage(value);
  }, []);

  const appendMessageToken = useCallback((token: string) => {
    setMessage((prev) => `${prev}${token}`);
  }, []);

  const handleSave = useCallback(() => {
    if (!message.trim()) {
      toast.error("Please enter a message");
      return;
    }

    if (filters.length === 0) {
      toast.error("Please add at least one condition");
      return;
    }

    const invalidFilters = filters.filter((f) => {
      if (isValuelessOperator(f.operator)) {
        return false;
      }
      if (isMessageFilterKnownValue(f.value)) {
        return false;
      }
      return !f.value;
    });

    if (invalidFilters.length > 0) {
      toast.error("Please fill in all condition fields for all filter rules");
      return;
    }

    onSave(filters, message);
  }, [filters, message, onSave]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose();
        }
      }}
    >
      <Dialog.Content size="large">
        <Dialog.Title>
          {editingRule ? "Edit Filter Rule" : "Create Filter Rule"}
        </Dialog.Title>

        <Flex
          direction="column"
          gap="6"
          p="4"
          className={filterRuleDialogStyles.dialogScrollBody}
        >
          {!fieldOptions || fieldOptions.length === 0 ? (
            <Text size="2" color="gray">
              <Em>No context fields available.</Em>
            </Text>
          ) : (
            <>
              <Step
                index={1}
                label="Conditions"
                description="Define when these rules should trigger"
              >
                <Flex direction="column" gap="4" width="100%">
                  {filters.map((filter, filterIndex) => (
                    <FilterRuleConditionRow
                      key={`${filter.id}-${filterIndex}`}
                      filter={filter}
                      fieldConfig={getFieldConfig(filter.field)}
                      contextFields={contextFields}
                      onUpdate={(updates) =>
                        updateFilter(filterIndex, updates)
                      }
                      onRemove={() => removeFilter(filterIndex)}
                    />
                  ))}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        color="gray"
                        variant="surface"
                        highContrast
                        size="2"
                        className={filterRuleDialogStyles.addConditionButton}
                      >
                        <PlusIcon />
                        Add Condition
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {fieldOptions.map((field) => (
                        <DropdownMenuItem
                          key={field.id}
                          onClick={() => addCondition(field)}
                        >
                          {field.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </Flex>
              </Step>

              <FilterRuleMessageStep
                message={message}
                onMessageChange={handleMessageChange}
                onAppendKnownToken={appendMessageToken}
                knownPlaceholders={knownMessagePlaceholders}
              />
            </>
          )}

          <Flex justify="end" gap="2" pt="4">
            <Button highContrast variant="soft" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              highContrast
              variant="solid"
              onClick={handleSave}
              disabled={isSavingFilter}
            >
              {editingRule ? "Update Filter Rule" : "Create Filter Rule"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
